/**
 * V70G8I — 엔글 타자왕 연습 게임 (토큰 미차감 · 기록 미저장)
 *
 * 공식 도전(도전 시작!)은 서버 consumeAcidRainSession 이 도전 횟수(토큰)를 차감하고 seed 를 발급한 뒤
 * 종료 시 submitAcidRainScore 로 기록·리더보드에 반영한다.
 * 연습은 **서버를 전혀 호출하지 않는다**: 브라우저에서 seed 를 만들고 같은 엔진·설정·채점으로 진행하며,
 * 세션에 practice=true 를 달아 기록 제출 경로를 원천 차단한다 (AcidRainGamePage.runSubmit 가드).
 */
import type { AcidRainSettings, GameResult } from './types';
import type { AcidRainServerInputEvent } from './eventConverter';

export const PRACTICE_SESSION_PREFIX = 'practice-';

export interface PracticeSession {
  sessionId: string;
  seed: string;
  startedAtServerMs: number;
  settings: AcidRainSettings;
  leaderboardEpoch: number;
  practice: true;
}

export function isPracticeSessionId(sessionId: string | null | undefined): boolean {
  return typeof sessionId === 'string' && sessionId.startsWith(PRACTICE_SESSION_PREFIX);
}

function randomToken(): string {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c?.getRandomValues) {
      const buf = new Uint32Array(2);
      c.getRandomValues(buf);
      return Array.from(buf, (n) => n.toString(36)).join('');
    }
  } catch {
    /* fall through */
  }
  return Math.random().toString(36).slice(2, 12);
}

/**
 * 연습 세션 생성 — 게임 규칙(시간·생명력·난이도 상승)은 공식 도전과 같은 설정을 그대로 사용.
 * (공식 세션 설정은 서버 상수이며 FE DEFAULT_SETTINGS 와 같은 값이다: 120초 / 생명력 5 / 10초마다 ×1.1)
 */
export function createPracticeSession(settings: AcidRainSettings, nowMs: number = Date.now()): PracticeSession {
  const sessionId = `${PRACTICE_SESSION_PREFIX}${nowMs}-${randomToken()}`;
  return {
    sessionId,
    seed: `${sessionId}:${randomToken()}`,
    startedAtServerMs: nowMs,
    settings: { ...settings, enabled: true },
    leaderboardEpoch: 0,
    practice: true,
  };
}

// ─────────── 결과 지표 ───────────

const COMPOUND_VOWELS = new Set([9, 10, 11, 14, 15, 16, 19]); // ㅘㅙㅚㅝㅞㅟㅢ
const COMPOUND_FINALS = new Set([3, 5, 6, 9, 10, 11, 12, 13, 14, 15, 18]); // ㄳㄵㄶㄺㄻㄼㄽㄾㄿㅀㅄ

/** 두벌식 기준 타건 수 (쌍자음·ㅒㅖ 는 Shift 조합 1타, 겹모음·겹받침 2타, 한글 외 문자 1타) */
export function keystrokesOf(text: string): number {
  let n = 0;
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (code >= 0xac00 && code <= 0xd7a3) {
      const idx = code - 0xac00;
      const jung = Math.floor((idx % 588) / 28);
      const jong = idx % 28;
      n += 1; // 초성
      n += COMPOUND_VOWELS.has(jung) ? 2 : 1;
      if (jong > 0) n += COMPOUND_FINALS.has(jong) ? 2 : 1;
    } else if (!/\s/.test(ch)) {
      n += 1;
    }
  }
  return n;
}

export interface PracticeStats {
  /** 분당 타수 (제거한 단어의 타건 수 ÷ 플레이 시간) */
  typingSpeed: number;
  /** 정확도 % (정답 제출 ÷ 전체 Enter 제출). 제출이 없으면 null */
  accuracy: number | null;
  correctSubmits: number;
  wrongSubmits: number;
}

export function computePracticeStats(
  result: Pick<GameResult, 'elapsedMs' | 'cleared'>,
  inputEvents: readonly Pick<AcidRainServerInputEvent, 'type' | 'word'>[] | undefined,
  wrongSubmits: number,
): PracticeStats {
  const clearedWords = (inputEvents ?? []).filter((e) => e.type === 'cleared').map((e) => e.word);
  const strokes = clearedWords.reduce((sum, w) => sum + keystrokesOf(w), 0);
  const minutes = result.elapsedMs / 60000;
  const typingSpeed = minutes >= 1 / 60 ? Math.round(strokes / minutes) : 0;
  const correct = result.cleared;
  const wrong = Math.max(0, wrongSubmits);
  const total = correct + wrong;
  return {
    typingSpeed,
    accuracy: total > 0 ? Math.round((correct / total) * 1000) / 10 : null,
    correctSubmits: correct,
    wrongSubmits: wrong,
  };
}
